// Carrier Contacts tab — FE-SCRM-030.
//
// Lists every CarrierContact attached to one Carrier with a status +
// role_type chip strip, sortable by name / role / last_touchpoint /
// status, and Edit / Delete actions per row (gated on the caller's
// ``canManageCarriers`` flag).

import { useMemo, useState } from "react";

import {
  Badge, Table, TBody, THead, Tr, Th as DsTh, Td as DsTd,
} from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import {
  CARRIER_CONTACT_ROLE_LABEL,
  CARRIER_CONTACT_STATUS_LABEL,
  formatRelativeTouchpoint,
  formatUsPhone,
  isTouchpointCold,
} from "./labels";
import { useCarrierContactsQuery } from "./hooks";
import {
  CARRIER_CONTACT_ROLE_TYPES,
  CARRIER_CONTACT_STATUSES,
  type CarrierContact,
  type CarrierContactRoleType,
  type CarrierContactStatus,
} from "./types";


type SortKey =
  | "name" | "role_type" | "last_touchpoint_at" | "status";
type SortDir = "asc" | "desc";


interface Props {
  carrierId: string;
  canManageCarriers: boolean;
  /** "Add contact" lives on the page header (CarrierDetailHeader) so
   * the tab itself doesn't render the button. The handler is still
   * threaded in so the future empty-state CTA can fire it without
   * passing the prop through twice. */
  onAddContact: () => void;
  onEditContact: (contact: CarrierContact) => void;
  onDeleteContact: (contact: CarrierContact) => void;
}


export function CarrierContactsTab({
  carrierId,
  canManageCarriers,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAddContact: _onAddContact,
  onEditContact,
  onDeleteContact,
}: Props) {
  const query = useCarrierContactsQuery(carrierId);
  const [statusFilter, setStatusFilter] = useState<
    CarrierContactStatus | "all"
  >("active");
  const [roleFilter, setRoleFilter] = useState<CarrierContactRoleType | "all">(
    "all",
  );
  const [sortBy, setSortBy] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const rows = useMemo<CarrierContact[]>(() => {
    const all = query.data?.rows ?? [];
    const filtered = all.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (roleFilter !== "all" && c.role_type !== roleFilter) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "name") {
        return dir * (
          `${a.last_name} ${a.first_name}`.localeCompare(
            `${b.last_name} ${b.first_name}`,
          )
        );
      }
      if (sortBy === "role_type") {
        return dir * a.role_type.localeCompare(b.role_type);
      }
      if (sortBy === "status") {
        return dir * a.status.localeCompare(b.status);
      }
      // last_touchpoint_at — null sorts to bottom regardless of dir.
      const av = a.last_touchpoint_at ?? "";
      const bv = b.last_touchpoint_at ?? "";
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return dir * av.localeCompare(bv);
    });
    return sorted;
  }, [query.data, statusFilter, roleFilter, sortBy, sortDir]);

  function toggleSort(next: SortKey) {
    if (next === sortBy) {
      setSortDir((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(next);
      setSortDir("asc");
    }
  }

  if (query.isLoading) {
    return <DashboardLoadingState variant="table" rows={4} />;
  }
  if (query.isError) {
    return (
      <DashboardErrorState
        title="Couldn't load contacts"
        retry={() => query.refetch()}
      />
    );
  }

  return (
    <div
      data-testid="carrier-contacts-tab"
      className="flex flex-col gap-3"
    >
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="carrier-contacts-filters"
      >
        <StatusChips value={statusFilter} onChange={setStatusFilter} />
        <RoleSelect value={roleFilter} onChange={setRoleFilter} />
      </div>

      {rows.length === 0 ? (
        <DashboardEmptyState
          title="No contacts at this carrier yet"
          description={
            canManageCarriers
              ? "Add your first contact above."
              : "Your brokerage hasn't added any contacts at this carrier yet."
          }
        />
      ) : (
        <Table
          dividers
          data-testid="carrier-contacts-table"
          className="min-w-full divide-y divide-border-subtle"
          wrapperClassName="rounded-md border border-border-subtle"
        >
          <THead className="bg-recessed text-left text-xs uppercase tracking-wide text-text-tertiary">
            <Tr>
              <SortableTh
                label="Name" sortKey="name"
                current={sortBy} dir={sortDir} onClick={toggleSort}
              />
              <Th>Title</Th>
              <SortableTh
                label="Role" sortKey="role_type"
                current={sortBy} dir={sortDir} onClick={toggleSort}
              />
              <Th>Email / Phone</Th>
              <Th>Territory</Th>
              <SortableTh
                label="Status" sortKey="status"
                current={sortBy} dir={sortDir} onClick={toggleSort}
              />
              <SortableTh
                label="Last touchpoint" sortKey="last_touchpoint_at"
                current={sortBy} dir={sortDir} onClick={toggleSort}
              />
              {canManageCarriers && (
                <DsTh className="text-right" aria-label="Actions" />
              )}
            </Tr>
          </THead>
          <TBody className="bg-canvas">
            {rows.map((c) => (
              <ContactRow
                key={c.carrier_contact_id}
                contact={c}
                canManageCarriers={canManageCarriers}
                onEdit={() => onEditContact(c)}
                onDelete={() => onDeleteContact(c)}
              />
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}


function ContactRow({
  contact, canManageCarriers, onEdit, onDelete,
}: {
  contact: CarrierContact;
  canManageCarriers: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const rel = formatRelativeTouchpoint(contact.last_touchpoint_at);
  const cold = isTouchpointCold(contact.last_touchpoint_at);
  const fullName = `${contact.first_name} ${contact.last_name}`.trim();
  return (
    <Tr
      data-testid={`carrier-contact-row-${contact.carrier_contact_id}`}
    >
      <Td>
        <span className="font-medium text-text-primary">{fullName}</span>
      </Td>
      <Td>
        <span className="text-text-secondary">{contact.title ?? "—"}</span>
      </Td>
      <Td>
        <Badge variant="neutral">
          {CARRIER_CONTACT_ROLE_LABEL[contact.role_type]
            ?? contact.role_type}
        </Badge>
      </Td>
      <Td>
        <div className="flex flex-col text-xs text-text-secondary">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="hover:text-text-primary"
            >
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone.replace(/[^\d+]/g, "")}`}
              className="hover:text-text-primary"
            >
              {formatUsPhone(contact.phone) || contact.phone}
            </a>
          )}
          {!contact.email && !contact.phone && "—"}
        </div>
      </Td>
      <Td>
        <span className="text-text-secondary">
          {[contact.territory, contact.carrier_office]
            .filter(Boolean)
            .join(" · ") || "—"}
        </span>
      </Td>
      <Td>
        <ContactStatusBadge status={contact.status} />
      </Td>
      <Td>
        <span
          className={
            !rel
              ? "text-text-tertiary"
              : cold
                ? "text-text-tertiary"
                : "text-text-secondary"
          }
          data-testid={`carrier-contact-last-touchpoint-${contact.carrier_contact_id}`}
        >
          {rel ?? "—"}
        </span>
      </Td>
      {canManageCarriers && (
        <Td>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              data-testid={`carrier-contact-edit-${contact.carrier_contact_id}`}
              onClick={onEdit}
              className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-primary transition-fast ease-out hover:bg-recessed"
            >
              Edit
            </button>
            <button
              type="button"
              data-testid={`carrier-contact-delete-${contact.carrier_contact_id}`}
              onClick={onDelete}
              className="rounded-md border border-border-subtle px-2 py-1 text-xs text-ember transition-fast ease-out hover:bg-recessed"
            >
              Delete
            </button>
          </div>
        </Td>
      )}
    </Tr>
  );
}


function ContactStatusBadge({
  status,
}: { status: CarrierContact["status"] }) {
  // active = sage/teal, inactive = neutral, left_carrier = ember per
  // the spec ("green for active, gray for inactive, amber for
  // left_carrier"). We map onto the existing Badge variants where
  // they exist.
  const variant: "category" | "neutral" | "regulatory" =
    status === "active"
      ? "category"
      : status === "left_carrier"
        ? "regulatory"
        : "neutral";
  return (
    <Badge variant={variant}>
      {CARRIER_CONTACT_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}


function StatusChips({
  value, onChange,
}: {
  value: CarrierContactStatus | "all";
  onChange: (next: CarrierContactStatus | "all") => void;
}) {
  const opts: Array<{ value: CarrierContactStatus | "all"; label: string }> = [
    { value: "active", label: "Active" },
    ...CARRIER_CONTACT_STATUSES
      .filter((s) => s !== "active")
      .map((s) => ({
        value: s,
        label: CARRIER_CONTACT_STATUS_LABEL[s],
      })),
    { value: "all", label: "All" },
  ];
  return (
    <div
      role="group"
      aria-label="Filter contacts by status"
      data-testid="carrier-contacts-status-chips"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {opts.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            data-testid={`carrier-contacts-status-${opt.value}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function RoleSelect({
  value, onChange,
}: {
  value: CarrierContactRoleType | "all";
  onChange: (next: CarrierContactRoleType | "all") => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-text-secondary">
      <span className="sr-only">Filter contacts by role</span>
      <select
        data-testid="carrier-contacts-role-select"
        value={value}
        onChange={(e) =>
          onChange(e.target.value as CarrierContactRoleType | "all")
        }
        className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-xs text-text-primary outline-none focus:border-teal focus:shadow-focus"
      >
        <option value="all">All roles</option>
        {CARRIER_CONTACT_ROLE_TYPES.map((r) => (
          <option key={r} value={r}>
            {CARRIER_CONTACT_ROLE_LABEL[r]}
          </option>
        ))}
      </select>
    </label>
  );
}


function Th({ children }: { children: React.ReactNode }) {
  return <DsTh>{children}</DsTh>;
}


function SortableTh({
  label, sortKey, current, dir, onClick,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (next: SortKey) => void;
}) {
  const active = current === sortKey;
  return (
    <DsTh>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        data-testid={`carrier-contacts-sort-${sortKey}`}
        aria-sort={
          active ? (dir === "asc" ? "ascending" : "descending") : "none"
        }
        className={[
          "inline-flex items-center gap-1 text-xs uppercase tracking-wide",
          active ? "text-text-primary" : "text-text-tertiary hover:text-text-primary",
        ].join(" ")}
      >
        {label}
        {active && (
          <span aria-hidden="true">{dir === "asc" ? "↑" : "↓"}</span>
        )}
      </button>
    </DsTh>
  );
}


function Td({ children }: { children: React.ReactNode }) {
  return <DsTd>{children}</DsTd>;
}
